import {
  Button,
  Collapse,
  DatePicker,
  Form,
  Modal,
  Select,
  Tag,
  Upload,
  UploadFile,
  List,
  Spin,
  Tooltip,
} from 'antd';
import { IProcessItem } from '../../interfaces/Workflow';
import { useContext, useEffect, useRef, useMemo, useState } from 'react';
import TextArea from 'antd/lib/input/TextArea';
import { PaperClipOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ContextApp } from '../../contexts/ContextApp';
import { EnumsValues } from '../../enums/EnumsValues';
import GraphqlService from '../../services/graphql/GraphqlService';
import { User } from 'firebase/auth';
import { Mutation } from '../../services/graphql/mutation';
import { notificationContext } from '../../contexts/NotificationContext';
import MomentTimezoneService from '../../services/moment-timezone/MomentTimezoneService';
import moment from 'moment';
import './ViewWorkflowModal.less';
import { UploadDragAndDrop } from '../../components/common/UploadDragAndDrop/UploadDragAndDrop';
import { message } from 'antd';
import { RcFile, UploadProps } from 'antd/es/upload';
import FinishedActivityDetails from './FinishedActivityDetails';
import OnlySaveBtnGroup from './ButtonsGroups/OnlySaveBtnGroup';
import FinalStepBtnGroup from './ButtonsGroups/FinalStepBtnGroup';
import RejectAndContinue from './ButtonsGroups/RejectAndContinue';
import RejectEndsFlowBtnGroup from './ButtonsGroups/RejectEndsFlowBtnGroup';

interface ViewProcessModalProps {
  state: any;
  onCancel?: () => void;
  data: IProcessItem | null;
  tenantId: number | undefined;
  onCompleteActivity?: (activityId: string) => void;
}

interface IWorkflowFile {
  id: string;
  filename: string;
  original_filename: string;
  mime_type: string;
  url: string;
  its: string;
}

const { Panel } = Collapse;
const { Option } = Select;

const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILE_COUNT = 5;

const ViewWorkflowModal = (props: ViewProcessModalProps) => {
  const { state, onCancel, data, tenantId, onCompleteActivity } = props;
  const { customRequest, Query, customFileRequest } = GraphqlService();
  const { t, user } = useContext(ContextApp);
  const { openNotification } = useContext(notificationContext);
  const [step, setStep] = useState(0);
  const [form] = Form.useForm();
  const [nextActivityData, setNextActivityData] = useState({
    approval: '',
    decline: '',
  });
  const [isRejected, setIsRejected] = useState({
    result: false,
    nextTask: '',
  });
  const [partialFormValues, setPartialFormValues] = useState({});
  const [tenantUsers, setTenantUsers] = useState<User[]>([]);
  const nextUser = Form.useWatch('nextUser', form);
  const { getDate } = MomentTimezoneService();
  const formattedInitialDate = getDate({ element: moment(data?.its).toDate() });
  const formattedDueDate = getDate({
    element: moment(data?.limitDate).toDate(),
  });
  const isPendingActivity = data?.status.id === EnumsValues.ConstNumbers.one;
  const [fileList, setFileList] = useState<UploadFile<RcFile>[]>([]);
  const isUploadingRef = useRef(false);
  const uploadQueueRef = useRef<UploadFile[]>([]);
  const [files, setFiles] = useState<IWorkflowFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(false);

  const handleCompleteActivity = (
    values: any,
    flowId: string | undefined,
    isRejectedFlag?: boolean,
  ) => {
    const rejected = isRejectedFlag ?? !isRejected.result;

    customRequest({
      mutation: Mutation.completeTask,
      variables: {
        input: {
          taskId: data?.id,
          nextUser: values.nextUser ? values.nextUser : user?.id.toString(),
          approved: rejected,
        },
      },
    })
      .then((response) => {
        if (response.message === 'error')
          throw new Error('Error al completar la tarea');

        if (!values.limitDate) {
          return;
        }

        // si se establecio fecha límite, buscar actividades activas
        return customRequest({
          query: Query.getActiveActivitiesByInstance,
          variables: {
            input: {
              processInstanceId: flowId,
            },
          },
        })
          .then((result) => {
            const activeTasks = result || [];
            const firstActiveTask = activeTasks?.[0];

            if (!firstActiveTask?.id) {
              return;
            }

            const utcStringDate = values.limitDate.utc().toISOString();

            return customRequest({
              mutation: Mutation.setDueDateToTask, //asignar fecha a la actividad recien creada
              variables: {
                input: {
                  taskId: firstActiveTask.id,
                  dueDate: utcStringDate,
                  tenantId: tenantId?.toString(),
                },
              },
            });
          })
          .catch((error) => {
            throw new Error(error);
          });
      })
      .then(() => {
        openNotification({
          type: 'success',
          msj: 'Actividad completada con éxito.',
          context: 'ViewWorkflowModal.completeTask',
        });
      })
      .catch((_error) => {
        openNotification({
          type: 'error',
          msj: 'Ocurrió un error al completar la actividad.',
          context: 'ViewWorkflowModal.completeTask',
        });
      })
      .finally(() => {
        if (data?.id) {
          onCompleteActivity?.(data.id);
        }
        resetModal();
      });
  };

  const getTenantUsers = async () => {
    try {
      await customRequest({
        query: Query.users,
        variables: {
          filter: {
            tenants: [tenantId],
          },
        },
      }).then((response) => {
        const data: any = [];

        const filteredData = response.filter(
          (user: any) =>
            user.user_role[0].role.name !==
              EnumsValues.SystemRolesNames.APIPublic &&
            user.user_role[0].role.name !==
              EnumsValues.SystemRolesNames.APIUser,
        );

        filteredData.forEach((user: any) => {
          data.push({
            id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
          });
        });

        setTenantUsers(data);
      });
    } catch (error) {}
  };

  const updateActivityDescription = async (observation: string) => {
    if (observation) {
      try {
        await customRequest({
          mutation: Mutation.addCommentToTask,
          variables: {
            input: {
              taskId: data?.id,
              message: observation,
            },
          },
        }).then(async (_response) => {
          openNotification({
            type: 'success',
            msj: 'Observaciones y archivos guardados con éxito.',
            context: 'ViewWorkflowModal.completeTask',
          });
        });
      } catch (error) {
        openNotification({
          type: 'error',
          msj: 'Ocurrió un error al actualizar la tarea.',
          context: 'ViewWorkflowModal.addCommentToTask',
        });
      }
    }

    if (fileList.length > 0) {
      const files = Array.from(fileList);
      const fileArray = files.map((fileItem, index) => ({
        file: fileItem.originFileObj as RcFile,
        path: `variables.files.${index}`,
      }));

      const variables = {
        files: fileArray.map(() => null), // This creates an array of nulls with the same length as files
        input: {
          workflowInstanceId: data?.flowId,
          taskId: data?.id,
        },
      };

      try {
        const response = await customFileRequest(
          {
            mutation: Mutation.uploadWorkflowFiles,
            variables: variables,
          },
          fileArray,
        );
        console.log('Workflow files upload response:', JSON.stringify(response));

        if (response?.uploadWorkflowFiles?.message) {
          openNotification({
            type: 'success',
            msj: 'Archivos subidos correctamente',
          });
          // Refresh the files list after successful upload
          if (onCompleteActivity) {
            onCompleteActivity(data?.id || '');
          }
        }
      } catch (error: any) {
        console.error('Error uploading files:', error);
        openNotification({
          type: 'error',
          msj: error.message || 'Error al subir los archivos',
        });
      }
    }
  };

  const getNextActivityData = async (task_id: string | undefined) => {
    try {
      await customRequest({
        query: Query.getNextActivityData,
        variables: {
          input: {
            taskId: task_id,
          },
        },
      }).then((response) => {
        const parsedResponse = JSON.parse(response.message);
        setNextActivityData(parsedResponse);
        return parsedResponse;
      });
    } catch (error) {}
  };

  useEffect(() => {
    if (data?.id) {
      getNextActivityData(data.id);
      fetchFilesForTask(data.id);
    }
  }, [data?.id]);

  const resetModal = () => {
    form.resetFields();
    onCancel?.();
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    const isValidSize = file.size <= MAX_FILE_SIZE;
    if (!isValidSize) {
      message.error(
        `El archivo "${file.name}" excede el tamaño máximo permitido.`,
      );
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const processNextFile = () => {
    if (isUploadingRef.current || uploadQueueRef.current.length === 0) return;
    if (fileList.filter((f) => f.status === 'done').length >= MAX_FILE_COUNT)
      return;

    isUploadingRef.current = true;
    const currentFile = uploadQueueRef.current.shift();

    setFileList((prevList) =>
      prevList.map((f: UploadFile) => {
        if (f.uid === currentFile?.uid) {
          return { ...f, status: 'done' };
        }
        return f;
      }),
    );
    isUploadingRef.current = false;
    processNextFile();
  };

  const handleFileChange: UploadProps['onChange'] = ({
    fileList: newFileList,
  }) => {
    const totalDone = newFileList.filter((f) => f.status === 'done').length;
    const totalQueued = uploadQueueRef.current.length;

    const totalAllowed = MAX_FILE_COUNT - totalDone - totalQueued;

    // filtra archivos que están en 'uploading' para limitar según el máximo permitido
    const uploading = newFileList.filter((f) => f.status === 'uploading');
    const accepted = uploading.slice(0, totalAllowed);
    const rejected = uploading.slice(totalAllowed);

    if (rejected.length > 0) {
      openNotification({
        type: 'error',
        msj: `Se excedió el límite de ${MAX_FILE_COUNT} archivos. Se descartaron ${rejected.length} archivos.`,
        context: 'dragAndDrop.workflows',
      });
    }

    //filtra archivos que ya están 'done' y los aceptados para subir
    const updatedList = [
      ...newFileList.filter((f) => f.status === 'done'),
      ...accepted,
    ];

    setFileList(updatedList);
  };

  useEffect(() => {
    const uploadingFiles = fileList.filter((f) => f.status === 'uploading');
    const queuedUids = uploadQueueRef.current.map((f) => f.uid);

    const newFilesToQueue = uploadingFiles.filter(
      (f) => !queuedUids.includes(f.uid),
    );

    if (newFilesToQueue.length > 0) {
      uploadQueueRef.current.push(...newFilesToQueue);
      processNextFile();
    }
  }, [fileList]);

  const uploadProps: UploadProps = {
    multiple: true,
    fileList,
    beforeUpload,
    onChange: handleFileChange,
    accept: '.pdf,.jpg,.jpeg,.png',
    listType: 'picture',
    showUploadList: true,
    customRequest: ({ file, onSuccess }) => {
      console.log(file);
      // Necesario para que AntD lo marque como "uploading"
      onSuccess && onSuccess('ok');
    },
  };

  const flowType = useMemo(() => {
    const { approval, decline } = nextActivityData;
    if (!decline.length) return 'onlySave';
    if (approval === 'end' && decline === 'end') return 'finalStep';
    if (decline && decline !== 'end') return 'rejectAndContinue';
    if (decline === 'end' && approval !== 'end') return 'rejectEndsFlow';
    return 'default';
  }, [nextActivityData]);

  const handleStepZeroSubmit = async (
    isRejectedValue: boolean,
    nextTask: string,
    finalize?: boolean,
  ) => {
    try {
      const values = await form.validateFields(['observations', 'archivos']);
      setPartialFormValues(values);
      setIsRejected({ result: isRejectedValue, nextTask });
      updateActivityDescription(values.observations);

      if (finalize) {
        handleCompleteActivity(values, data?.flowId, !isRejectedValue);
      } else {
        setStep(1);
        getTenantUsers();
      }
    } catch (err) {}
  };

  const fetchFilesForTask = async (taskId: string) => {
    if (!taskId) return;

    setLoadingFiles(true);
    try {
      // Use the previous_task_id from the task data if available
      const previousTaskId = data?.previous_task_id;
      
      if (previousTaskId) {
        const response = await customRequest({
          query: Query.getWorkflowFilesByTask,
          variables: {
            input: {
              taskId: previousTaskId,
              workflowInstanceId: data?.id,
            },
          },
        });

        if (response?.files) {
          setFiles([...(response.files || [])]);
        } else {
          setFiles([]);
        }
      } else {
        setFiles([]);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  return (
    <Modal
      footer={false}
      open={state}
      onCancel={onCancel}
      title={`${
        !step && isPendingActivity
          ? 'Actividad'
          : !step && !isPendingActivity
          ? 'Detalles de la actividad'
          : 'Asignar próxima actividad'
      }`}
      style={{ maxWidth: '597px' }}
    >
      {isPendingActivity ? (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            observations: `${data?.comment ? data.comment : ''}`,
          }}
        >
          {!step ? (
            <>
              <div className="activity-container">
                <div className="activity-header">
                  <h5 className="activity-name">{data?.activity}</h5>
                  <div className="status-container" style={{ display: 'inline-block', marginLeft: '10px' }}>
                    <span>{t('entity.status')}:</span>
                    <Tag color={data?.status.color}>{data?.status.name}</Tag>
                  </div>
                </div>
                
                <span className="activity-subtitle">
                  Pertenece al flujo: {data?.process} - Motivo:{' '}
                  {data?.reason ? data?.reason : t('entity.noReason')}
                </span>

                <div className="initial-date-container">
                  <label className="date-label">Asignado por:</label>
                  <span>{data?.assigner_name || '-'}</span>
                </div>

                <div className="initial-date-container">
                  <label className="date-label">{t('entity.startdate')}:</label>
                  <span>{formattedInitialDate}</span>
                </div>

                <div className="end-date-container">
                  <label className="date-label">Fecha límite:</label>
                  <span>{data?.limitDate ? formattedDueDate : 'Sin asignar'}</span>
                </div>

                <p style={{ marginTop: '15px' }}>
                  <b>{data?.description}:</b>
                </p>

                {data?.previous_task_comment && (
                  <div className="activity-data">
                    <span>
                      {data.previous_task_comment || 'Sin observaciones'}
                    </span>
                  </div>
                )}

                <div className="attachments-section">
                  <div className="attachments-header">
                    <b><PaperClipOutlined /> {t('entity.attachments')}:</b>
                  </div>
                  {loadingFiles ? (
                    <div className="loading-container">
                      <Spin size="small" />
                    </div>
                  ) : files?.length > 0 ? (
                    <List
                      size="small"
                      dataSource={files}
                      renderItem={(file: IWorkflowFile) => (
                        <List.Item>
                          <span style={{ color: '#999', fontSize: '12px' }}>
                            {moment(file.its).format('DD/MM/YYYY HH:mm')}
                          </span>
        
                          <Tooltip title={file.original_filename || file.filename}>
                            <span
                              style={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                paddingLeft: '20px',
                              }}
                            >
                              {file.original_filename || file.filename}
                            </span>
                          </Tooltip>
        
                          <Tooltip title={t('entity.download')}>
                            <Button
                              type="text"
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                              }}                    
                            >
                              <DownloadOutlined style={{ color: '#03729F' }} />
                            </Button>
                          </Tooltip>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <p>No hay archivos adjuntos</p>
                  )}
                </div>

                <Collapse className="collapse-panel">
                  <Panel header="Agregar información para completar esta actividad" key="1">
                    <UploadDragAndDrop
                      uploadProps={uploadProps}
                      maxPagePerDocument=""
                    />
                    <Form.Item
                      name="observations"
                      label={`${t('entity.observations')} (Opcional)`}
                      style={{ marginTop: 15 }}
                    >
                      <TextArea maxLength={250} rows={3} />
                    </Form.Item>
                  </Panel>
                </Collapse>
              </div>

              {/* Button Groups */}
              {flowType === 'onlySave' && (
                <OnlySaveBtnGroup
                  onCancel={onCancel}
                  handleStepZeroSubmit={handleStepZeroSubmit}
                  nextActivityData={nextActivityData}
                />
              )}

              {flowType === 'finalStep' && (
                <FinalStepBtnGroup
                  onCancel={onCancel}
                  handleStepZeroSubmit={handleStepZeroSubmit}
                  nextActivityData={nextActivityData}
                />
              )}

              {flowType === 'rejectAndContinue' && (
                <RejectAndContinue
                  nextActivityData={nextActivityData}
                  handleStepZeroSubmit={handleStepZeroSubmit}
                />
              )}

              {flowType === 'rejectEndsFlow' && (
                <RejectEndsFlowBtnGroup
                  nextActivityData={nextActivityData}
                  handleStepZeroSubmit={handleStepZeroSubmit}
                />
              )}
            </>
          ) : (
            // Step 1: Assign next activity
            <>
              <div className="flow-info">
                <div className="flow-detail">
                  <label>{t('entity.flow')}:</label>
                  <span>{data?.process}</span>
                </div>
                <div className="next-activity">
                  <label>Próxima {t('entity.activity').toLowerCase()}:</label>
                  <span>{isRejected?.nextTask}</span>
                </div>
              </div>

              <Form.Item
                label={t('entity.limitDate')}
                name="limitDate"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const today = dayjs().startOf('day');
                      const maxDate = data?.end_date_instance
                        ? dayjs(data.end_date_instance).endOf('day')
                        : null;
                      const dateValue = dayjs(value).startOf('day');

                      if (dateValue.isBefore(today)) {
                        return Promise.reject(
                          new Error('La fecha no puede ser anterior a hoy')
                        );
                      }

                      if (maxDate && dateValue.isAfter(maxDate)) {
                        return Promise.reject(
                          new Error(
                            `La fecha no puede ser posterior a la fecha límite asignada al proceso (${maxDate.format(
                              'DD-MM-YYYY'
                            )})`
                          )
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <DatePicker
                  format="DD-MM-YYYY"
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                  placeholder="Opcional"
                  allowClear={true}
                />
              </Form.Item>

              <Form.Item 
                label={`${t('action.assign')} a:`}
                name="nextUser"
                rules={[{ required: true, message: 'Por favor selecciona un usuario' }]}
              >
                <Select
                  placeholder="Selecciona un usuario"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input: string, option: any) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {tenantUsers.map((user: any) => (
                    <Option key={user.id} value={user.id.toString()}>
                      {`${user.firstname} ${user.lastname}`.trim()}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <div className="button-container">
                <Button onClick={() => setStep(0)}>
                  {t('action.return')}
                </Button>
                <Button 
                  type="primary" 
                  onClick={async () => {
                    try {
                      const values = await form.validateFields();
                      const fullFormData = {
                        ...partialFormValues,
                        ...values,
                      };
                      handleCompleteActivity(fullFormData, data?.flowId);
                    } catch (error) {
                      console.error('Validation failed:', error);
                    }
                  }}
                  disabled={!nextUser}
                >
                  {t('action.assign')}
                </Button>
              </div>
            </>
          )}
        </Form>
      ) : (
        // Show read-only view for completed activities
        <FinishedActivityDetails
          data={data}
          formattedDueDate={formattedDueDate}
          formattedInitialDate={formattedInitialDate}
          onCancel={onCancel}
        />
      )}
    </Modal>
  );
};

export default ViewWorkflowModal;
